import { OverlayType } from '@shared/models/googlemaps.model';

declare const google: any;

export interface NativeDrawingOptions {
  circleOptions?: any;
  rectangleOptions?: any;
  polygonOptions?: any;
  polylineOptions?: any;
}

export interface NativeOverlayCompleteEvent {
  type: OverlayType;
  overlay: any;
}

/**
 * Drop-in replacement for `google.maps.drawing.DrawingManager`, which was
 * decommissioned in the Maps JavaScript API v3.65 (May 2026). It reproduces the
 * interactive drawing behaviour for circles, rectangles, polygons and polylines
 * using native `google.maps` overlays plus map mouse events, and emits an
 * `overlaycomplete`-style event (`{ type, overlay }`) that is compatible with the
 * existing shape handling in the map component. All shape persistence, editing,
 * saved-search restore and search-query conversion continue to use the native
 * overlays unchanged.
 */
export class NativeDrawingManager {
  private drawingMode: OverlayType | null = null;
  private readonly modeListeners: Array<any> = [];
  private previewOverlay: any = null;
  private startLatLng: any = null;
  private vertices: Array<any> = [];
  private isDragging: boolean = false;

  private originalMapOptions: { draggable: boolean; draggableCursor: string | null; disableDoubleClickZoom: boolean } = {
    draggable: true,
    draggableCursor: null,
    disableDoubleClickZoom: false
  };

  /** Minimum drag distance (meters) below which a drag shape is discarded (treated as a stray click). */
  private readonly MIN_DRAG_DISTANCE_METERS: number = 1;

  constructor(
    private readonly map: any,
    private readonly options: NativeDrawingOptions,
    private readonly onOverlayComplete: (event: NativeOverlayCompleteEvent) => void
  ) { }

  public getDrawingMode(): OverlayType | null {
    return this.drawingMode;
  }

  /**
   * Activates (or clears with `null`) an interactive drawing mode. Mirrors
   * `DrawingManager.setDrawingMode`. Selecting the currently active mode is a no-op.
   */
  public setDrawingMode(mode: OverlayType | null): void {
    if (mode === this.drawingMode) {
      return;
    }

    this.reset();
    this.drawingMode = mode;

    if (!mode) {
      this.restoreMapInteraction();
      return;
    }

    this.disableMapInteraction();

    if (mode === OverlayType.CIRCLE || mode === OverlayType.RECTANGLE) {
      this.startDragShape();
    } else if (mode === OverlayType.POLYGON || mode === OverlayType.POLYLINE) {
      this.startVertexShape();
    } else {
      // MARKER and any other type are not drawn interactively.
      this.setDrawingMode(null);
    }
  }

  /** Cancels any in-progress drawing and detaches mode listeners. Completed overlays stay on the map. */
  public reset(): void {
    this.clearModeListeners();

    if (this.previewOverlay) {
      this.previewOverlay.setMap(null);
      this.previewOverlay = null;
    }

    this.startLatLng = null;
    this.vertices = [];
    this.isDragging = false;
  }

  /** Frees listeners and restores map interaction. Call on component destroy. */
  public dispose(): void {
    this.reset();
    this.restoreMapInteraction();
  }

  // ---- drag-based shapes: circle & rectangle ----

  private startDragShape(): void {
    this.modeListeners.push(
      this.map.addListener('mousedown', (e: any) => {
        this.isDragging = true;
        this.startLatLng = e.latLng;
        this.previewOverlay = this.createDragPreview(this.startLatLng);
        this.previewOverlay.setMap(this.map);
      }),
      this.map.addListener('mousemove', (e: any) => {
        if (!this.isDragging || !this.previewOverlay) {
          return;
        }
        this.updateDragPreview(this.startLatLng, e.latLng);
      }),
      this.map.addListener('mouseup', (e: any) => {
        if (!this.isDragging) {
          return;
        }
        this.isDragging = false;
        this.finishDragShape(e.latLng);
      })
    );
  }

  private createDragPreview(center: any): any {
    if (this.drawingMode === OverlayType.CIRCLE) {
      return new google.maps.Circle({
        ...(this.options.circleOptions || {}),
        editable: false,
        draggable: false,
        clickable: false,
        center,
        radius: 0
      });
    }

    return new google.maps.Rectangle({
      ...(this.options.rectangleOptions || {}),
      editable: false,
      draggable: false,
      clickable: false,
      bounds: this.boundsFromPoints(center, center)
    });
  }

  private updateDragPreview(start: any, current: any): void {
    if (this.drawingMode === OverlayType.CIRCLE) {
      this.previewOverlay.setRadius(google.maps.geometry.spherical.computeDistanceBetween(start, current));
    } else {
      this.previewOverlay.setBounds(this.boundsFromPoints(start, current));
    }
  }

  private finishDragShape(end: any): void {
    const preview = this.previewOverlay;
    this.previewOverlay = null;

    if (!preview) {
      this.setDrawingMode(null);
      return;
    }

    const distance = this.startLatLng && end
      ? google.maps.geometry.spherical.computeDistanceBetween(this.startLatLng, end)
      : 0;

    preview.setMap(null);

    if (distance < this.MIN_DRAG_DISTANCE_METERS) {
      // A plain click with no drag: nothing to create.
      this.setDrawingMode(null);
      return;
    }

    const overlay = this.drawingMode === OverlayType.CIRCLE
      ? new google.maps.Circle({
        ...(this.options.circleOptions || {}),
        center: preview.getCenter(),
        radius: preview.getRadius()
      })
      : new google.maps.Rectangle({
        ...(this.options.rectangleOptions || {}),
        bounds: preview.getBounds()
      });

    overlay.setMap(this.map);
    this.complete(overlay);
  }

  private boundsFromPoints(a: any, b: any): any {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(a);
    bounds.extend(b);
    return bounds;
  }

  // ---- vertex-based shapes: polygon & polyline ----

  private startVertexShape(): void {
    this.vertices = [];
    this.previewOverlay = this.createVertexPreview();
    this.previewOverlay.setMap(this.map);

    this.modeListeners.push(
      this.map.addListener('click', (e: any) => {
        this.vertices.push(e.latLng);
        this.previewOverlay.setPath([...this.vertices]);
      }),
      this.map.addListener('mousemove', (e: any) => {
        if (!this.vertices.length) {
          return;
        }
        // Rubber-band preview: committed vertices plus the current cursor point.
        this.previewOverlay.setPath([...this.vertices, e.latLng]);
      }),
      this.map.addListener('dblclick', () => this.finishVertexShape())
    );
  }

  private createVertexPreview(): any {
    const opts = this.drawingMode === OverlayType.POLYGON
      ? (this.options.polygonOptions || {})
      : (this.options.polylineOptions || {});

    // The in-progress preview is always an open polyline so partial shapes are visible.
    // It must be non-clickable so vertex clicks and the finishing double-click reach the map
    // instead of being intercepted by the preview line.
    return new google.maps.Polyline({ ...opts, editable: false, draggable: false, clickable: false });
  }

  private finishVertexShape(): void {
    const isPolygon = this.drawingMode === OverlayType.POLYGON;
    const minVertices = isPolygon ? 3 : 2;
    const points = this.dedupeConsecutive(this.vertices);

    if (this.previewOverlay) {
      this.previewOverlay.setMap(null);
      this.previewOverlay = null;
    }

    if (points.length < minVertices) {
      this.vertices = [];
      this.setDrawingMode(null);
      return;
    }

    const overlay = isPolygon
      ? new google.maps.Polygon({ ...(this.options.polygonOptions || {}), paths: points })
      : new google.maps.Polyline({ ...(this.options.polylineOptions || {}), path: points });

    overlay.setMap(this.map);
    this.vertices = [];
    this.complete(overlay);
  }

  private dedupeConsecutive(points: Array<any>): Array<any> {
    return points.filter((point: any, index: number) => {
      if (index === 0) {
        return true;
      }
      const previous = points[index - 1];
      return point.lat() !== previous.lat() || point.lng() !== previous.lng();
    });
  }

  // ---- shared ----

  private complete(overlay: any): void {
    const type = this.drawingMode;
    this.clearModeListeners();
    this.drawingMode = null;
    this.restoreMapInteraction();
    this.onOverlayComplete({ type, overlay });
  }

  private clearModeListeners(): void {
    while (this.modeListeners.length) {
      google.maps.event.removeListener(this.modeListeners.pop());
    }
  }

  private disableMapInteraction(): void {
    this.originalMapOptions = {
      draggable: this.map.get('draggable') !== false,
      draggableCursor: this.map.get('draggableCursor') ?? null,
      disableDoubleClickZoom: this.map.get('disableDoubleClickZoom') === true
    };

    this.map.setOptions({ draggable: false, draggableCursor: 'crosshair', disableDoubleClickZoom: true });
  }

  private restoreMapInteraction(): void {
    this.map.setOptions({
      draggable: this.originalMapOptions.draggable,
      draggableCursor: this.originalMapOptions.draggableCursor,
      disableDoubleClickZoom: this.originalMapOptions.disableDoubleClickZoom
    });
  }
}
